import { useState, useMemo } from 'react';
import type { Entity } from '../types/api';

export const useComponentSpecifications = (entities: Entity[]) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [typeSearchTerms, setTypeSearchTerms] = useState<Record<string, string>>({});
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const allModels = useMemo(() => {
    const models = new Set<string>();
    entities.forEach(entity => {
      const metadata = entity.entity_metadata || entity.metadata || {};
      const modelValue = metadata.Model || metadata.model || metadata.MODEL;
      if (modelValue && typeof modelValue === 'string') {
        models.add(modelValue);
      }
    });
    return Array.from(models).sort();
  }, [entities]);

  const allCategories = useMemo(() => {
    const categories = new Set<string>();
    entities.forEach(entity => {
      const category = entity.concept_category || entity.entity_type_name || 'Unknown';
      categories.add(category);
    });
    return Array.from(categories).sort();
  }, [entities]);

  const filteredEntities = useMemo(() => {
    return entities.filter(entity => {
      const name = entity.name || entity.entity_name || '';
      const category = entity.concept_category || entity.entity_type_name || '';
      const metadata = entity.entity_metadata || entity.metadata || {};
      const entityModel = metadata.Model || metadata.model || metadata.MODEL || '';

      if (searchTerm && !(
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entityModel.toLowerCase().includes(searchTerm.toLowerCase())
      )) {
        return false;
      }

      if (selectedModel && entityModel !== selectedModel) {
        return false;
      }

      if (selectedCategory && category !== selectedCategory) {
        return false;
      }

      return true;
    });
  }, [entities, searchTerm, selectedModel, selectedCategory]);

  const entitiesByType = useMemo(() => {
    const grouped: Record<string, Entity[]> = {};
    filteredEntities.forEach(entity => {
      const type = entity.concept_category || entity.entity_type_name || 'Unknown';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(entity);
    });
    return grouped;
  }, [filteredEntities]);

  const sortedTypes = useMemo(() => {
    return Object.keys(entitiesByType).sort((a, b) => {
      const diff = entitiesByType[b].length - entitiesByType[a].length;
      return diff !== 0 ? diff : a.localeCompare(b);
    });
  }, [entitiesByType]);

  const toggleType = (type: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  const updateTypeSearch = (type: string, value: string) => {
    setTypeSearchTerms(prev => ({ ...prev, [type]: value }));
  };

  const selectModel = (model: string) => {
    setSelectedModel(model);
    setSelectedCategory('');
  };

  const selectCategory = (category: string) => {
    setSelectedCategory(category);
    setSelectedModel('');
  };

  return {
    searchTerm,
    setSearchTerm,
    expandedTypes,
    typeSearchTerms,
    selectedModel,
    selectedCategory,
    allModels,
    allCategories,
    filteredEntities,
    entitiesByType,
    sortedTypes,
    toggleType,
    updateTypeSearch,
    selectModel,
    selectCategory,
  };
};
